import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import { AppDispatch, RootState } from '../store/store';
import {
  fetchReferrals,
  fetchReferralsDropdownData,
  setFilters as setReferralsFilters,
  setPage as setReferralsPage,
  setPageSize as setReferralsPageSize,
  setSorting as setReferralsSorting,
} from '../features/lma/referralsSlice';
import {
  fetchNonReferrals,
  fetchNonReferralsDropdownData,
  setFilters as setNonReferralsFilters,
  setPage as setNonReferralsPage,
  setPageSize as setNonReferralsPageSize,
  setSorting as setNonReferralsSorting,
} from '../features/lma/nonReferralsSlice';
import {
  fetchProcessedReferrals,
  fetchProcessedReferralsDropdownData,
  setFilters as setProcessedReferralsFilters,
  setPage as setProcessedReferralsPage,
  setPageSize as setProcessedReferralsPageSize,
  setSorting as setProcessedReferralsSorting,
} from '../features/lma/processedReferralsSlice';
import type { FetchReferralDataPayload } from '../services/leadManagementApi';

// Build lead status filter params from Sitecore config
const buildLeadStatusParams = (leadStatusFilters: any[]): Record<string, string> => {
  if (!leadStatusFilters || !Array.isArray(leadStatusFilters)) {
    return {};
  }

  const params: Record<string, string> = {};

  leadStatusFilters.forEach((filter: any) => {
    const name = filter.name?.toLowerCase(); // "hot", "warm", "cold"
    const lowerLimit = filter.fields?.LSFDaysLowerLimit?.value;
    const upperLimit = filter.fields?.LSFDaysUpperLimit?.value;

    if (name && lowerLimit !== undefined) {
      // Use static keys for each lead type
      if (name === 'hot' && upperLimit !== undefined) {
        params['hotLead'] = `${lowerLimit}-${upperLimit}`;
      } else if (name === 'warm' && upperLimit !== undefined) {
        params['warmLead'] = `${lowerLimit}-${upperLimit}`;
      } else if (name === 'cold') {
        // Cold lead only uses lower limit
        params['coldLead'] = lowerLimit;
      }
    }
  });

  return params;
};

export const useLMAReferrals = (leadStatusFilters: any[]) => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    data,
    totalRecords,
    loading,
    error,
    lastUpdated,
    currentPage,
    pageSize,
    filters,
    sortBy,
    sortOrder,
    dropdownData,
    dropdownLoading,
    dropdownError,
  } = useSelector((state: RootState) => state.lmaReferrals);

  const loadReferrals = useCallback(
    (customFilters?: Record<string, string>, page?: number, size?: number) => {
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        configuration: {
          leads: leadStatusParams,
        },
        filters: customFilters || filters,
        sortBy,
        sortOrder,
      };

      const params = { PageNo: page || currentPage, PageSize: size || pageSize };
      dispatch(fetchReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, filters, currentPage, pageSize, sortBy, sortOrder]
  );

  const applyFilters = useCallback(
    (newFilters: Record<string, string>) => {
      // Only dispatch fetchReferrals - don't call setReferralsFilters separately
      // The filters will be updated in Redux when the API call is initiated
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        configuration: {
          leads: leadStatusParams,
        },
        filters: newFilters,
        sortBy,
        sortOrder,
      };

      // Update filters in Redux state synchronously after API call (convert to strings)
      dispatch(setReferralsFilters(newFilters));
      dispatch(setReferralsPage(1)); // Also update page to 1

      const params = { PageNo: 1, PageSize: pageSize };
      // Then dispatch the API call
      dispatch(fetchReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, pageSize, sortBy, sortOrder]
  );

  const changePage = useCallback(
    (page: number) => {
      dispatch(setReferralsPage(page));
      // Automatically fetch with new page
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        configuration: {
          leads: leadStatusParams,
        },
        filters,
        sortBy,
        sortOrder,
      };

      const params = { PageNo: page, PageSize: pageSize };
      // Then dispatch the API call
      dispatch(fetchReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, filters, pageSize, sortBy, sortOrder]
  );

  const changePageSize = useCallback(
    (size: number) => {
      dispatch(setReferralsPageSize(size));
      // Automatically fetch with new page size
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        configuration: {
          leads: leadStatusParams,
        },
        filters,
        sortBy,
        sortOrder,
      };

      const params = { PageNo: 1, PageSize: size };
      // Then dispatch the API call
      dispatch(fetchReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, filters, sortBy, sortOrder]
  );

  const changeSorting = useCallback(
    (field: string, order: 'asc' | 'desc') => {
      dispatch(setReferralsSorting({ sortBy: field, sortOrder: order }));
      dispatch(setReferralsPage(1)); // Reset page to 1 when sorting changes
      // Automatically fetch with new sorting
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        configuration: {
          leads: leadStatusParams,
        },
        filters,
        sortBy: field,
        sortOrder: order,
      };

      const params = { PageNo: 1, PageSize: pageSize };
      dispatch(fetchReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, filters, currentPage, pageSize]
  );

  const loadDropdownData = useCallback(() => {
    dispatch(fetchReferralsDropdownData(leadStatusFilters));
  }, [dispatch, leadStatusFilters]);

  return {
    data,
    totalRecords,
    loading,
    error,
    lastUpdated,
    currentPage,
    pageSize,
    filters,
    sortBy,
    sortOrder,
    dropdownData,
    dropdownLoading,
    dropdownError,
    loadReferrals,
    loadDropdownData,
    applyFilters,
    changePage,
    changePageSize,
    changeSorting,
  };
};

export const useLMANonReferrals = (leadStatusFilters: any[]) => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    data,
    totalRecords,
    loading,
    error,
    lastUpdated,
    currentPage,
    pageSize,
    filters,
    sortBy,
    sortOrder,
    dropdownData,
    dropdownLoading,
    dropdownError,
  } = useSelector((state: RootState) => state.lmaNonReferrals);

  const loadNonReferrals = useCallback(
    (customFilters?: Record<string, string>, page?: number, size?: number) => {
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        leadStatusParams,
        filters: customFilters || filters,
        pagination: {
          page: page || currentPage,
          pageSize: size || pageSize,
        },
        sortBy,
        sortOrder,
      };
      dispatch(fetchNonReferrals(payload));
    },
    [dispatch, leadStatusFilters, filters, currentPage, pageSize, sortBy, sortOrder]
  );

  const applyFilters = useCallback(
    (newFilters: Record<string, string>) => {
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        leadStatusParams,
        filters: newFilters,
        pagination: {
          page: 1,
          pageSize,
        },
        sortBy,
        sortOrder,
      };

      dispatch(setNonReferralsFilters(newFilters));
      dispatch(setNonReferralsPage(1));
      dispatch(fetchNonReferrals(payload));
    },
    [dispatch, leadStatusFilters, pageSize, sortBy, sortOrder]
  );

  const changePage = useCallback(
    (page: number) => {
      dispatch(setNonReferralsPage(page));
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        leadStatusParams,
        filters,
        pagination: {
          page,
          pageSize,
        },
        sortBy,
        sortOrder,
      };
      dispatch(fetchNonReferrals(payload));
    },
    [dispatch, leadStatusFilters, filters, pageSize, sortBy, sortOrder]
  );

  const changePageSize = useCallback(
    (size: number) => {
      dispatch(setNonReferralsPageSize(size));
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        leadStatusParams,
        filters,
        pagination: {
          page: 1,
          pageSize: size,
        },
        sortBy,
        sortOrder,
      };
      dispatch(fetchNonReferrals(payload));
    },
    [dispatch, leadStatusFilters, filters, sortBy, sortOrder]
  );

  const changeSorting = useCallback(
    (field: string, order: 'asc' | 'desc') => {
      dispatch(setNonReferralsSorting({ sortBy: field, sortOrder: order }));
      // Automatically fetch with new sorting
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        leadStatusParams,
        filters,
        pagination: {
          page: currentPage,
          pageSize,
        },
        sortBy: field,
        sortOrder: order,
      };
      dispatch(fetchNonReferrals(payload));
    },
    [dispatch, leadStatusFilters, filters, currentPage, pageSize]
  );

  const loadDropdownData = useCallback(() => {
    dispatch(fetchNonReferralsDropdownData(leadStatusFilters));
  }, [dispatch, leadStatusFilters]);

  return {
    data,
    totalRecords,
    loading,
    error,
    lastUpdated,
    currentPage,
    pageSize,
    filters,
    sortBy,
    sortOrder,
    dropdownData,
    dropdownLoading,
    dropdownError,
    loadNonReferrals,
    loadDropdownData,
    applyFilters,
    changePage,
    changePageSize,
    changeSorting,
  };
};

export const useLMAProcessedReferrals = (leadStatusFilters: any[]) => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    data,
    totalRecords,
    loading,
    error,
    lastUpdated,
    currentPage,
    pageSize,
    filters,
    sortBy,
    sortOrder,
    dropdownData,
    dropdownLoading,
    dropdownError,
  } = useSelector((state: RootState) => state.lmaProcessedReferrals);

  const loadProcessedReferrals = useCallback(
    (customFilters?: Record<string, string>, page?: number, size?: number) => {
      const payload: FetchReferralDataPayload = {
        filters: customFilters || filters,
        sortBy,
        sortOrder,
      };

      const params = { PageNo: page || currentPage, PageSize: size || pageSize };
      dispatch(fetchProcessedReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, filters, currentPage, pageSize, sortBy, sortOrder]
  );

  const applyFilters = useCallback(
    (newFilters: Record<string, string>) => {
      const payload: FetchReferralDataPayload = {
        filters: newFilters,
        sortBy,
        sortOrder,
      };

      dispatch(setProcessedReferralsFilters(newFilters));
      dispatch(setProcessedReferralsPage(1));

      const params = { PageNo: 1, PageSize: pageSize };
      dispatch(fetchProcessedReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, pageSize, sortBy, sortOrder]
  );

  const changePage = useCallback(
    (page: number) => {
      dispatch(setProcessedReferralsPage(page));
      const payload: FetchReferralDataPayload = {
        filters,
        sortBy,
        sortOrder,
      };

      const params = { PageNo: page, PageSize: pageSize };
      dispatch(fetchProcessedReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, filters, pageSize, sortBy, sortOrder]
  );

  const changePageSize = useCallback(
    (size: number) => {
      dispatch(setProcessedReferralsPageSize(size));
      const payload: FetchReferralDataPayload = {
        filters,
        sortBy,
        sortOrder,
      };

      const params = { PageNo: 1, PageSize: size };
      dispatch(fetchProcessedReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, filters, sortBy, sortOrder]
  );

  const changeSorting = useCallback(
    (field: string, order: 'asc' | 'desc') => {
      dispatch(setProcessedReferralsSorting({ sortBy: field, sortOrder: order }));
      dispatch(setProcessedReferralsPage(1));
      const payload: FetchReferralDataPayload = {
        filters,
        sortBy: field,
        sortOrder: order,
      };

      const params = { PageNo: 1, PageSize: pageSize };
      dispatch(fetchProcessedReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, filters, currentPage, pageSize]
  );

  const loadDropdownData = useCallback(() => {
    dispatch(fetchProcessedReferralsDropdownData(leadStatusFilters));
  }, [dispatch, leadStatusFilters]);

  return {
    data,
    totalRecords,
    loading,
    error,
    lastUpdated,
    currentPage,
    pageSize,
    filters,
    sortBy,
    sortOrder,
    dropdownData,
    dropdownLoading,
    dropdownError,
    loadProcessedReferrals,
    loadDropdownData,
    applyFilters,
    changePage,
    changePageSize,
    changeSorting,
  };
};
